// Google Ads (gtag.js) helpers.
//
// The base tag is loaded once in public/index.html:
//   <script async src="https://www.googletagmanager.com/gtag/js?id=AW-18383330959"></script>
//   gtag('js', new Date()); gtag('config', 'AW-18383330959');
//
// This module is the only place in the app that touches window.gtag, so
// components can track page views / conversions without worrying about the
// script being blocked, still loading, or missing during SSR-less tests.

export const GOOGLE_ADS_ID = "AW-18383330959";

/**
 * Conversion labels are issued per conversion action in
 * Google Ads -> Goals -> Conversions -> (action) -> Tag setup, and look like
 * "AbC-D_efGhIjKlMnOp". They are set as build-time env vars so a new
 * conversion action does not require a code change.
 */
export const conversionLabels = {
  /** Fired when a visitor clicks an App Store / Google Play download button. */
  appDownload: process.env.REACT_APP_GOOGLE_ADS_APP_DOWNLOAD_LABEL,
};

type GtagFn = (...args: any[]) => void;

function getGtag(): GtagFn | undefined {
  if (typeof window === "undefined") return undefined;
  const fn = (window as any).gtag;
  return typeof fn === "function" ? (fn as GtagFn) : undefined;
}

/** True when the gtag script is present (it is absent if an ad blocker removed it). */
export function isGtagAvailable(): boolean {
  return Boolean(getGtag());
}

/** Low-level passthrough. Silently no-ops when gtag is unavailable. */
export function gtagCall(...args: any[]): void {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    gtag(...args);
  } catch {
    // Analytics must never break the UI.
  }
}

/** Generic event helper. */
export function gtagEvent(name: string, params: Record<string, any> = {}): void {
  gtagCall("event", name, params);
}

/**
 * Register a page view for a client-side route change.
 * The very first page view is already sent by the config call in index.html,
 * so callers should skip the initial render (see GtagRouteTracker).
 */
export function trackPageView(path: string, title?: string): void {
  gtagCall("config", GOOGLE_ADS_ID, {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    page_title: title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
}

/**
 * Report a Google Ads conversion.
 *
 * @param label conversion label from Google Ads (see conversionLabels)
 * @param params optional value/currency/transaction_id
 */
export function trackConversion(
  label: string | undefined,
  params: Record<string, any> = {}
): void {
  if (!label) {
    // No label configured yet -> nothing to send. Warn in dev so the missing
    // env var is obvious, stay quiet in production.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[gtag] trackConversion called without a conversion label; skipping.");
    }
    return;
  }

  gtagEvent("conversion", {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    ...params,
  });
}
